/* @flow */

import React from 'react';

const DEFAULT_INTERVAL = 1000;

type Props = {
    shouldPoll: boolean,
    startPolling: (number) => void,
    stopPolling: () => void,
    interval?: number,
    onPollComplete?: () => Promise<*> | void,
};

/**
 * A hook that manages polling via apollo client's built-in polling
 * functionality.
 *
 * Beware, skip conditions have been known to cause issues with this hook.
 */
export function usePoll(props: Props) {
    const {
        shouldPoll,
        startPolling,
        stopPolling,
        interval = DEFAULT_INTERVAL,
        onPollComplete,
    } = props;

    const currentlyPolling = React.useRef(false);

    React.useEffect(() => {
        if (shouldPoll && !currentlyPolling.current) {
            startPolling(interval);
            currentlyPolling.current = true;
        }
        if (!shouldPoll && currentlyPolling.current) {
            stopPolling();
            currentlyPolling.current = false;
        }

        // If the component unmounts before our shouldPoll state turns false,
        // ensure we stop polling
        return () => {
            stopPolling();
            currentlyPolling.current = false;
            if (onPollComplete) {
                onPollComplete();
            }
        };
    }, [shouldPoll, startPolling, stopPolling, interval, onPollComplete]);
}
